import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import SignupModal from '../components/SignupModal';

import './LoginPage.css';

const LoginPage: React.FC = () => {
  const { login, isLoading, error } = useAuth();
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [pendingUserData, setPendingUserData] = useState<any>(null);
  const [googleState, setGoogleState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    // Google OAuth 스크립트 로드
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    script.onerror = () => setGoogleState('error');

    script.onload = () => {
      if (!window.google?.accounts) {
        setGoogleState('error');
        return;
      }
      if (window.google && window.google.accounts) {
        try {
          window.google.accounts.id.initialize({
            client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID || '836425439040-ann0lm2g5idq74t7jplqe1ndh19a1ftb.apps.googleusercontent.com',
            callback: handleGoogleLogin,
            cancel_on_tap_outside: false,
            auto_select: false,
            itp_support: true,
            // 모든 환경에서 popup 모드를 씁니다. redirect 모드는 Google이 /auth/callback으로 POST를 보내는데,
            // 정적 호스팅(S3/CloudFront)은 이를 처리할 수 없고 Google 콘솔에 redirect URI 등록도 따로 필요합니다.
            ux_mode: 'popup',
          });

          // 버튼 렌더링
          const buttonElement = document.getElementById('google-signin-button');
          if (buttonElement) {
            window.google.accounts.id.renderButton(buttonElement, {
              theme: 'filled_black',
              size: 'large',
              width: 320,
              text: 'continue_with',
              shape: 'pill',
              locale: 'ko',
            });
            setGoogleState('ready');
          }

        } catch (error) {
          setGoogleState('error');
          console.error('Google OAuth initialization failed:', error);
        }
      }
    };

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handleGoogleLogin = async (response: any) => {
    try {
      if (!response || !response.credential) {
        console.error('Invalid Google response:', response);
        throw new Error('유효하지 않은 Google 인증 응답');
      }

      console.log('Google login response received:', { hasCredential: !!response.credential });
      const result = await login(response.credential);

      if (result === null) {
        // 로그인 성공 - 자동으로 리다이렉트됨
        console.log('Login successful, redirecting...');
        window.location.href = '/dashboard';
      }
    } catch (err) {
      console.error('Google login failed:', err);
      // 사용자에게 에러 메시지 표시
    }
  };

  const handleSignupConfirm = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userData: pendingUserData })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          localStorage.setItem('userId', result.data.user_id);
          localStorage.setItem('authToken', result.data.token);
          window.location.reload(); // 페이지 새로고침
        }
      }
    } catch (err) {
      console.error('Signup failed:', err);
    } finally {
      setShowSignupModal(false);
      setPendingUserData(null);
    }
  };

  const handleSignupCancel = () => {
    setShowSignupModal(false);
    setPendingUserData(null);
  };



  return (
    <>
      <div className="login-page">
        <header className="login-page__topbar">
          <span className="login-page__brand">
            <span className="login-page__mark" aria-hidden="true">AWS</span>
            AWS Exam Practice
          </span>
        </header>

        <main className="login-page__hero">
          <section className="login-page__intro">
            <p className="login-page__eyebrow">AWS 자격증 모의시험</p>
            <h1 className="login-page__title">
              실제 시험처럼 풀고,
              <br />
              약한 영역부터 다시 봅니다.
            </h1>
            <p className="login-page__lead">
              SAA·SAP·DVA·CLF 등 AWS 자격증을 실제 시간과 문항 수로 연습하세요.
              중간에 멈춰도 이어서 풀 수 있고, 끝나면 문항별 해설이 남습니다.
            </p>

            <div className="login-page__signin">
              <div id="google-signin-button" className="login-page__google" />
              {googleState === 'loading' && <span className="login-page__status" role="status">Google 로그인 버튼을 불러오는 중...</span>}
              {googleState === 'error' && (
                <p className="login-page__error" role="alert">
                  Google 로그인을 불러오지 못했습니다. 광고·추적 차단 확장 프로그램을 끄거나 새로고침해 주세요.
                </p>
              )}
              {isLoading && <span className="login-page__status" role="status">로그인 처리 중...</span>}
              {error && (
                <p className="login-page__error" role="alert">
                  {error} 잠시 후 다시 시도하거나 다른 Google 계정으로 로그인해 주세요.
                </p>
              )}
              <p className="login-page__note">Google 계정으로 바로 시작합니다. 별도 가입 절차는 없습니다.</p>
            </div>
          </section>

          <section className="login-page__preview" aria-label="시험 화면 미리보기">
            <div className="login-preview">
              <div className="login-preview__head">
                <span className="login-preview__code">SAA-C03</span>
                <span className="login-preview__timer">남은 시간 1:24:10</span>
              </div>
              <div className="login-preview__progress"><span style={{ width: '42%' }} /></div>
              <p className="login-preview__q">
                사용자를 지연 시간이 가장 짧은 리전으로 보내고, 리전 장애 시 자동으로 장애 조치하려면?
              </p>
              <ul className="login-preview__options">
                <li className="is-selected"><span>1</span>Route 53 지연 시간 기반 라우팅 + 상태 확인</li>
                <li><span>2</span>리전별 ALB와 교차 리전 대상 그룹</li>
                <li><span>3</span>Global Accelerator 가중치만 설정</li>
              </ul>
            </div>
            <div className="login-preview login-preview--result">
              <span className="login-preview__label">최근 결과</span>
              <strong className="login-preview__score tabular">82%</strong>
              <span className="login-preview__pass">합격</span>
              <span className="login-preview__weak">약한 영역 · 비용 최적화</span>
            </div>
          </section>
        </main>

        <section className="login-page__features" aria-label="주요 기능">
          <article>
            <h3>실전과 같은 조건</h3>
            <p>시험별 문항 수와 제한 시간, 플래그와 검토 흐름을 그대로 제공합니다.</p>
          </article>
          <article>
            <h3>자동 저장과 이어 풀기</h3>
            <p>답안은 문항마다 저장됩니다. 다른 기기에서도 이어서 풀 수 있습니다.</p>
          </article>
          <article>
            <h3>문항별 해설과 약점 분석</h3>
            <p>끝나면 정답 근거와 영역별 정답률로 다음에 볼 범위를 알려줍니다.</p>
          </article>
        </section>

        <footer className="login-page__footer">
          <span>AWS Exam Practice</span>
          <span>이 사이트는 Amazon Web Services와 공식적으로 연계되어 있지 않습니다.</span>
        </footer>
      </div>

      <SignupModal
        isOpen={showSignupModal}
        userData={pendingUserData || { email: '', name: '', google_id: '' }}
        onConfirm={handleSignupConfirm}
        onCancel={handleSignupCancel}
      />
    </>
  );
};

// Google OAuth 타입 정의
declare global {
  interface Window {
    google: any;
  }
}

export default LoginPage;
